import { useCallback, useEffect, useRef, useState } from "react";
import { AdminPage } from "@/components/AdminPage";
import { AdminTable, type Column } from "@/components/AdminTable";
import { OverviewMetricChart } from "@/components/OverviewMetricChart";
import { useToast } from "@/components/Toast";
import { api } from "../lib/api";
import type { ApiContract } from "@podecontratar/shared/contract/index";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";
import { AlertCircle, Ban, Check, CloudOff, MoreHorizontal, Power, RotateCcw } from "lucide-react";

type UserStatus = "active" | "inactive" | "banned";

type Row = EndpointOutput<ApiContract["GET /admin/users"]>[number];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso.replace(" ", "T") + "Z").toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatCPF(d: string | null): string {
  if (!d || d.length !== 11) return d ?? "—";
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

const COLUMNS: Column<Row>[] = [
  {
    header: "Usuário",
    width: "22%",
    cell: (r) => (
      <div>
        <p className="truncate font-medium text-text">{r.name}</p>
        <p className="truncate text-[11px] text-text-tertiary">{r.email}</p>
      </div>
    ),
  },
  {
    header: "Status",
    width: "9%",
    cell: (r) => <StatusBadge row={r} />,
  },
  {
    header: "CPF / Telefone",
    width: "13%",
    hideBelow: "sm",
    cell: (r) => (
      <div>
        <p className="truncate text-xs text-text-secondary">{formatCPF(r.cpf)}</p>
        <p className="truncate text-[11px] text-text-tertiary">{r.phone ?? "—"}</p>
      </div>
    ),
  },
  {
    header: "PJs",
    width: "7%",
    hideBelow: "md",
    cell: (r) => (
      <span
        title={`${r.pj_entities_count} empresa(s) cadastradas. PF padrão não contada.`}
        className="text-xs text-text"
      >
        {r.pj_entities_count}
      </span>
    ),
  },
  {
    header: "Indicações",
    width: "8%",
    hideBelow: "xl",
    cell: (r) => (
      <div>
        <p className="text-xs text-text">
          {r.referrals_paid}
          <span className="text-text-tertiary"> / {r.referrals_total}</span>
        </p>
        <p className="truncate font-mono text-[10px] text-text-tertiary">
          {r.referral_code ?? "—"}
        </p>
      </div>
    ),
  },
  {
    header: "Total gasto",
    width: "9%",
    hideBelow: "md",
    cell: (r) => (
      <span className="text-xs font-medium text-text">
        {formatBRL(r.total_spent_cents ?? 0)}
      </span>
    ),
  },
  {
    header: "Stripe",
    width: "12%",
    hideBelow: "lg",
    cell: (r) => <StripeSyncBadge row={r} />,
  },
  {
    header: "Criado",
    width: "11%",
    hideBelow: "sm",
    cell: (r) => (
      <span className="text-[11px] text-text-secondary">{formatDate(r.created_at)}</span>
    ),
  },
];

/**
 * Three-state Stripe sync indicator.
 *   - synced  → green check, tooltip shows last_stripe_sync_at + customer id
 *   - error   → red alert, tooltip carries the persisted error string
 *   - pending → muted CloudOff, never synced
 */
function StripeSyncBadge({ row }: { row: Row }) {
  if (row.last_stripe_sync_error) {
    return (
      <span
        title={`Última falha: ${row.last_stripe_sync_error}`}
        className="inline-flex items-center gap-1 rounded-full bg-danger/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-danger"
      >
        <AlertCircle size={11} strokeWidth={2} aria-hidden />
        Erro
      </span>
    );
  }
  if (row.stripe_customer_id && row.last_stripe_sync_at) {
    return (
      <span
        title={`Sincronizado em ${formatDate(row.last_stripe_sync_at)} · ${row.stripe_customer_id}`}
        className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent"
      >
        <Check size={11} strokeWidth={2} aria-hidden />
        Sincronizado
      </span>
    );
  }
  if (row.stripe_customer_id) {
    return (
      <span
        title={`Customer ${row.stripe_customer_id} criado mas sem timestamp de sync. Próxima execução do cron preenche.`}
        className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-warning"
      >
        <CloudOff size={11} strokeWidth={2} aria-hidden />
        Pendente
      </span>
    );
  }
  return (
    <span
      title="Sem Customer no Stripe. Será criado no próximo login ou via cron diário."
      className="inline-flex items-center gap-1 rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-tertiary"
    >
      <CloudOff size={11} strokeWidth={2} aria-hidden />
      Não criado
    </span>
  );
}

/** Moderation state badge. `deleted_at` wins over the live status. */
function StatusBadge({ row }: { row: Row }) {
  if (row.deleted_at) {
    return (
      <span className="inline-flex items-center rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-tertiary">
        excluído
      </span>
    );
  }
  // Values are en-US; the badge a person reads is pt-BR, like the rest of the
  // admin copy.
  const meta =
    row.status === "banned"
      ? { label: "banido", cls: "bg-danger/10 text-danger" }
      : row.status === "inactive"
        ? { label: "inativo", cls: "bg-warning/10 text-warning" }
        : { label: "ativo", cls: "bg-accent-soft text-accent" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}

// The moves offered from each status, and how each target reads as an action.
const TRANSITIONS: Record<UserStatus, UserStatus[]> = {
  active: ["inactive", "banned"],
  inactive: ["active", "banned"],
  banned: ["active", "inactive"],
};
// Keys are the en-US status values; the labels stay pt-BR — they are what a
// person clicks.
const ACTION_LABEL: Record<UserStatus, string> = {
  active: "Reativar",
  inactive: "Inativar",
  banned: "Banir",
};
const ACTION_ICON: Record<UserStatus, typeof Ban> = {
  active: RotateCcw,
  inactive: Power,
  banned: Ban,
};

/**
 * Per-row kebab menu offering the valid status transitions. The dropdown is
 * fixed-positioned off the button's rect so the table's overflow can't clip
 * it, and closes on outside-click, Escape, or scroll. Deleted users get no
 * menu — they can't be moderated.
 */
function ModerationMenu({
  row,
  onModerate,
}: {
  row: Row;
  onModerate: (row: Row, next: UserStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !menuRef.current?.contains(t)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  if (row.deleted_at) return null;
  const current: UserStatus =
    row.status === "inactive" || row.status === "banned" ? row.status : "active";

  function toggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.right });
    }
    setOpen((v) => !v);
  }

  return (
    <div className="flex justify-end">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-label={`Ações para ${row.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-text-tertiary transition-colors hover:bg-surface-alt hover:text-text"
      >
        <MoreHorizontal size={16} strokeWidth={2} aria-hidden />
      </button>
      {open && coords && (
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            transform: "translateX(-100%)",
          }}
          className="z-50 min-w-[9rem] overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-[var(--shadow-card-lift)]"
        >
          {TRANSITIONS[current].map((next) => {
            const Icon = ACTION_ICON[next];
            const danger = next === "banned";
            return (
              <button
                key={next}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onModerate(row, next);
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-alt ${danger ? "text-danger" : "text-text"}`}
              >
                <Icon size={14} strokeWidth={2} aria-hidden />
                {ACTION_LABEL[next]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminUsersPage() {
  const toast = useToast();
  const [refreshKey, setRefreshKey] = useState(0);

  const onModerate = useCallback(
    async (row: Row, next: UserStatus) => {
      if (
        next === "banned" &&
        !confirm(`Banir ${row.name}? O acesso é bloqueado na próxima requisição.`)
      ) {
        return;
      }
      try {
        await api.request("PATCH /admin/users/:id/status", { params: { id: row.id }, body: { status: next } });
        toast.push(`${row.name} agora está ${next}.`, "success");
        setRefreshKey((k) => k + 1);
      } catch {
        toast.push("Não foi possível alterar o status.", "error");
      }
    },
    [toast]
  );

  const columns: Column<Row>[] = [
    ...COLUMNS,
    {
      header: "",
      width: "5%",
      cell: (r) => <ModerationMenu row={r} onModerate={onModerate} />,
    },
  ];

  return (
    <AdminPage
      eyebrow="Usuários"
      title="Cadastros"
      description="Todos os usuários do PodeContratar. Use a busca pra filtrar por nome, e-mail ou CPF."
    >
      <div className="mb-6">
        <OverviewMetricChart
          metric="users"
          title="Cadastros nos últimos 30 dias"
        />
      </div>
      <AdminTable<Row>
        fetchPage={({ q, cursor, limit }) =>
          api.request("GET /admin/users", { query: { q: q || undefined, limit, cursor } })
        }
        rowKey={(r) => r.id}
        columns={columns}
        refreshKey={refreshKey}
        searchPlaceholder="Nome, e-mail ou CPF"
        emptyTitle="Nenhum usuário cadastrado"
      />
    </AdminPage>
  );
}
